import React, { useState, FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  CreateBankTransferDto,
  GetRechnungDetailsDto,
} from "../../../../api-client";
import {
  recnungTransactionMethods,
  transactionTypes,
} from "../../../Transactions/Kasse/v2/types/kasse.types";
import { useCreateBankTransfer } from "../../../../stores/Transactions/transaction.mutations";
import {
  useLocationId,
  useUserBusinessId,
} from "../../../../stores/User/User.hooks";

const germanNumberFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface PayRechnungDialogProps {
  open: boolean;
  onClose: () => void;
  rechnung: GetRechnungDetailsDto | null;
  accountId?: string;
  userId?: string;
  onPaymentSuccess: () => void;
}

const PayRechnungDialog: FC<PayRechnungDialogProps> = ({
  open,
  onClose,
  rechnung,
  accountId,
  userId,
  onPaymentSuccess,
}) => {
  // Use the mutation hook instead of store
  const createBankTransferMut = useCreateBankTransfer();
  const businessId = useUserBusinessId();
  const locationId = useLocationId();

  // Track the rechnung ID to detect when rechnung changes
  const [lastRechnungId, setLastRechnungId] = useState<string | null>(null);
  const [payAmount, setPayAmount] = useState<number>(0);

  // Reset payAmount only when rechnung actually changes (different ID)
  const currentRechnungId = rechnung?.id ?? null;
  if (currentRechnungId !== lastRechnungId) {
    setLastRechnungId(currentRechnungId);
    setPayAmount(rechnung?.due ?? 0);
  }

  const handleConfirm = () => {
    if (!rechnung || !accountId || !userId) return;

    const dto: CreateBankTransferDto = {
      method: recnungTransactionMethods.BANK_TRANSFER,
      text: "Bezahlung für Rechnung " + rechnung.rechnungNumber,
      type: transactionTypes.EINNAHME,
      amount: payAmount,
      rechnungAllocations: [
        {
          rechnungId: rechnung.id,
          paidAmount: payAmount,
        },
      ],
      accountId: accountId,
      userId: userId,
      patientId: Number(rechnung.patientId) || undefined,
      rxId: rechnung.rxId,
      businessId: businessId,
      locationId: locationId,
    };

    createBankTransferMut.mutate(dto, {
      onSuccess: () => {
        onPaymentSuccess();
        onClose();
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Rechnung Bezahlen</DialogTitle>
      <DialogContent>
        {rechnung && (
          <>
            <p>Rechnungnummer: {rechnung.rechnungNumber}</p>
            <p>Rezeptnummer: {rechnung.rxNumber}</p>
            <p>Fällig: {germanNumberFormatter.format(rechnung.due)} €</p>
            <TextField
              label="Bezahlbetrag"
              type="number"
              value={payAmount}
              onChange={(e) => setPayAmount(parseFloat(e.target.value))}
              fullWidth
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={createBankTransferMut.isPending}
        >
          {createBankTransferMut.isPending ? (
            <>
              <CircularProgress size={16} color="inherit" />
              <span style={{ marginLeft: 8 }}>Bitte warten...</span>
            </>
          ) : (
            "Einreichen"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayRechnungDialog;
